// pages/features/SchedulingFeaturePage.jsx
import React from "react";
import { Link } from "react-router-dom";
import HomeHeader from "../modals/HomeHeader.js";

const EXPLORE_PATH = "/ProviderSearchPage";

export default function SchedulingFeaturePage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <HomeHeader />

      {/* HERO */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Scheduling &amp; Availability
            </h1>

            <p className="mt-3 text-lg text-gray-600 leading-relaxed">
              Make and manage appointments with real-time visibility into
              provider availability—including cancellations and waitlists.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              <Pill label="Availability" />
              <Pill label="Appointments" />
              <Pill label="Cancellations" accent="red" />
              <Pill label="No-shows" />
              <Pill label="Waitlists" accent="emerald" />
            </div>

            <div className="mt-8">
              <Link
                to={EXPLORE_PATH}
                className="inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
              >
                Explore how MedMaze works →
              </Link>
              <p className="mt-2 text-xs text-gray-500">
                See the live experience in Provider Search.
              </p>
            </div>
          </div>

          {/* Appointment Feed Illustration */}
          <div className="rounded-2xl border-2 border-teal-400 bg-white shadow-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="font-semibold text-gray-800">
                Upcoming Appointments
              </div>
              <div className="text-xs text-gray-400">Today — Feb 11</div>
            </div>

            <div className="divide-y divide-gray-100">
              {/* Appointment 1 — Confirmed */}
              <div className="px-5 py-3.5 flex items-center gap-4">
                <div className="flex flex-col items-center w-12 shrink-0">
                  <span className="text-xs font-semibold text-gray-500">
                    9:00
                  </span>
                  <span className="text-[10px] text-gray-400">AM</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    Dr. Amara Osei
                  </p>
                  <p className="text-xs text-gray-500">Primary Care · 30 min</p>
                </div>
                <StatusChip label="Confirmed" tone="teal" />
              </div>

              {/* Appointment 2 — Cancelled */}
              <div className="px-5 py-3.5 flex items-center gap-4 bg-red-50/40">
                <div className="flex flex-col items-center w-12 shrink-0">
                  <span className="text-xs font-semibold text-gray-500">
                    10:30
                  </span>
                  <span className="text-[10px] text-gray-400">AM</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-400 line-through truncate">
                    Dr. Kevin Tran
                  </p>
                  <p className="text-xs text-gray-400">Dermatology · 20 min</p>
                </div>
                <StatusChip label="Cancelled" tone="red" />
              </div>

              {/* Appointment 3 — Waitlist */}
              <div className="px-5 py-3.5 flex items-center gap-4">
                <div className="flex flex-col items-center w-12 shrink-0">
                  <span className="text-xs font-semibold text-gray-500">
                    1:00
                  </span>
                  <span className="text-[10px] text-gray-400">PM</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    Dr. Lisa Chen
                  </p>
                  <p className="text-xs text-gray-500">Psychiatry · 45 min</p>
                </div>
                <StatusChip label="Waitlist" tone="emerald" />
              </div>

              {/* Appointment 4 — Completed */}
              <div className="px-5 py-3.5 flex items-center gap-4">
                <div className="flex flex-col items-center w-12 shrink-0">
                  <span className="text-xs font-semibold text-gray-500">
                    3:15
                  </span>
                  <span className="text-[10px] text-gray-400">PM</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    Dr. James Holbrook
                  </p>
                  <p className="text-xs text-gray-500">Cardiology · 30 min</p>
                </div>
                <StatusChip label="Completed" tone="gray" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PANELS */}
      <section className="bg-white py-4 pb-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Provider panel */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h2 className="text-xl font-bold text-gray-900">
              Built for provider workflows
            </h2>
            <ul className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Manage availability and appointment slots in one place.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>Handle cancellations and no-shows without chaos.</span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Keep waitlists organized so openings don't go to waste.
                </span>
              </li>
            </ul>
          </div>

          {/* Patient panel */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h2 className="text-xl font-bold text-gray-900">
              Designed for patient clarity
            </h2>
            <ul className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
              <li className="flex gap-3">
                <BulletIcon />
                <span>View availability before choosing a provider.</span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Manage appointments and stay informed about changes.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Make more confident decisions with clearer timing and options.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* HELP STRIP */}
      <section className="bg-teal-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
            <h3 className="text-lg font-bold text-gray-900">
              How this helps in practice
            </h3>
            <p className="mt-2 text-sm text-gray-700 leading-relaxed max-w-3xl">
              By keeping schedules accurate and responsive, MedMaze reduces
              back-and-forth and helps the right appointments happen faster—so
              patients get clarity and providers stay in control.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white py-8 text-xs text-gray-500 text-center border-t">
        © {new Date().getFullYear()} MedMaze. All rights reserved.
      </footer>
    </div>
  );
}

function Pill({ label, accent }) {
  const base =
    "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold";
  const theme =
    accent === "red"
      ? "border-red-200 bg-red-50 text-red-700"
      : accent === "emerald"
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-teal-200 bg-teal-50 text-teal-700";

  return <span className={`${base} ${theme}`}>{label}</span>;
}

function StatusChip({ label, tone }) {
  const base =
    "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border";
  const theme =
    tone === "teal"
      ? "border-teal-200 bg-teal-50 text-teal-700"
      : tone === "red"
        ? "border-red-200 bg-red-50 text-red-700"
        : tone === "emerald"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-gray-200 bg-gray-50 text-gray-700";

  return <span className={`${base} ${theme}`}>{label}</span>;
}

function BulletIcon() {
  return (
    <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
      ✓
    </span>
  );
}
